import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { pdsCustomElement as customElement } from '../../decorators/pds-custom-element';
import { PdsElement } from '../PdsElement';
import styles from './footnote-item.scss?inline';
import '../link/link';
import { required } from '../../decorators/required';
import { requiredSlot } from '../../decorators/requiredSlot';

/**
 * @summary A footnote item is an item in the footnotes list.
 * It requires an id and href to properly link to a piece of content.
 *
 * @slot default Required: The footnote text
 */
@customElement('pds-footnote-item', {
  category: 'component',
  type: 'component',
  state: 'stable',
  styles,
})
export class PdsFootnoteItem extends PdsElement {
  connectedCallback() {
    super.connectedCallback();
    this.initLocalization();
  }

  /**
   * Style variant
   * - **default** renders the standard footnote-item used for footnotes
   */
  @property()
  variant: 'default';

  /**
   * - id for footnote item. This is a **required** property.
   */
  @required
  @property({ type: String, reflect: true })
  id: string;

  /**
   * Required
   * - href for footnote item. This is a **required** property.
   */
  @required
  @property({ type: String })
  href: string = '';

  @property({ type: String })
  ariaLabel: string;

  /**
   * When leaving the footnote item, remove the tabindex so it is no longer focusable and
   * preserves the correct focus order
   */
  handleBlur() {
    this.removeAttribute('tabindex');
  }

  /**
   * Focuses on previous item
   * For all `pds-link` components in the light DOM, match the footnote
   * item href and pds-link id.
   * If they match, focus on the `pds-c-link`. Set timeout is
   * needed to occur slightly after the link click
   */
  focusOnPreviousItem() {
    this.handleBlur();
    const allLinks = document.querySelectorAll('pds-footnote-link');
    allLinks.forEach((element) => {
      if (this.href === `#${element.id}`) {
        setTimeout(() => {
          /* Optional chaining prevents ability to get full jest coverage - https://github.com/istanbuljs/istanbuljs/issues/516 */
          /* istanbul ignore next */
          element.shadowRoot
            ?.querySelector<HTMLAnchorElement>('.pds-c-footnote-link')
            ?.focus();
        }, 1);
      }
    });
  }

  /**
   * @internal
   */
  get classNames() {
    return {
      [this.variant]: !!this.variant,
    };
  }

  @requiredSlot(['default'])
  render() {
    return html`
      <li class="${this.getClass()}" role="listitem">
        <div class="pds-c-footnote-item--highlight-container">
          <slot></slot>
          <pds-link
            variant="subtle"
            @click=${() => this.focusOnPreviousItem()}
            @blur=${this.handleBlur}
            class="pds-c-footnote-item--backlink"
            href="${this.href}"
            ariaLabel="${this.ariaLabel ||
            this.translateText('back-to-content')}"
            target="_self"
            >&#8617;</pds-link
          >
        </div>
      </li>
    `;
  }
}
